import React, { useEffect } from 'react';
import { Nav } from './nav.tsx';
import { Footer } from './footer.tsx';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import i18next from 'i18next';

type ParlaFunctions = {
  load: () => void;
  [key: `popup${number}`]: () => void;
}

type ParlaDoc = Document & {
  parla: ParlaFunctions;
}

export function HomePage() {
    const { lang } = useParams();
    const { t } = useTranslation();
    const baseURL = "https://www.parlaretail.com";
    useEffect(() => {
        if (lang) {
            i18next.changeLanguage(lang);
        }
    }, [lang]);
    return (
        <>
            <Helmet key={lang}>
                {/* General Meta */}
                <meta charSet="utf-8" />
                <meta http-equiv="X-UA-Compatible" content="IE=edge" />
                <title>{t('homepage.meta.title')}</title>
                <meta name="description" content={t('homepage.meta.description')} />
                <meta name="keywords" content={t('homepage.meta.keywords')} />
                <meta name="viewport" content="width=device-width, initial-scale=1" />

                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta property="og:title" content={t('homepage.meta.title')} />
                <meta property="og:description" content={t('homepage.meta.description')} />
                <meta property="og:url" content={`${baseURL}/${lang}/`} />
                <meta property="og:image" content={`${baseURL}/img/sharing-image.jpg`} />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={t('homepage.meta.title')} />
                <meta name="twitter:description" content={t('homepage.meta.description')} />
                <meta name="twitter:image" content={`${baseURL}/img/sharing-image.jpg`} />

                {/* Hreflang for Language Versioning */}
                <link rel="alternate" hrefLang="en" href={`${baseURL}/en/`} />
                <link rel="alternate" hrefLang="es" href={`${baseURL}/es/`} />

                {/* Favicon */}
                <link rel="icon" type="image/png" href={`${baseURL}/img/parla-fav.png`} />
            </Helmet>
            <Nav />
            <div className="page">
                {/* Hero Section */}
                <div className="section ptb-100">
                    <div className="hero-how">
                        <div className="hero-text w80">
                            <div className="title f60 mf40 t-center t-color-3 mb20">
                                {t('homepage.experience_title')}
                            </div>
                            <div className="sub-title t-center t-color-3 f20">
                                <span className="t-color-2 bold">
                                    {t('homepage.experience_subtitle')}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Video Section */}
                <div className="section-video">
                    <div className="row">
                        {/* Uncomment the video below when needed */}
                        <video muted autoPlay loop playsInline poster="/img/your-poster-image.png">
                            <source src="/img/PARLA_102024_V6.0.mp4" type="video/mp4" />
                            {t('homepage.video_not_supported')}
                        </video>
                    </div>
                    {/* <div className="btn-wrap mt50 center-in p20">
                        <a href="http://admin.parla.tech/signup" className="btn btn-dark">
                            {t('homepage.start_using')}
                        </a>
                    </div> */}
                </div>

                {/* Customer Experience Section */}
                <div className="section">
                    <div className="hero-how">
                        <div className="hero-text">
                            <div className="row">
                                <div className="title w100 f50 mf40 t-center t-color-2 mb20">
                                    {t('homepage.better_experience_title')}
                                </div>
                            </div>
                            <div className="row center">
                                <div className="w80 t-center">
                                    <div className="f20">
                                        {t('homepage.better_experience_description')}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Outperform Section */}
                <div className="section-color section-column">
                    <div className="row f40 t-center t-color-4 p20">
                        {t('homepage.outperform_title')}
                    </div>
                    <div className="text-info-panel w80">
                        <div className="item">
                            <div className="value t-color-4">{t('homepage.sales_conversion')}</div>
                            <div className="description t-color-4">
                                {t('homepage.sales_conversion_description')}
                            </div>
                        </div>
                        <div className="item">
                            <div className="value t-color-4">{t('homepage.higher_aov')}</div>
                            <div className="description t-color-4">
                                {t('homepage.higher_aov_description')}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Testimonial Section */}
                <div className="section-column">
                    <div className="italic t-color-2 f60 mf40 w80 mt100 t-center p20">
                        {t('homepage.testimonial_quote')}
                    </div>
                    <div className="italic t-color-2 f20 w80 mt50 t-center">
                        {t('homepage.testimonial_author')}
                    </div>
                    <div className="row logos-customers">
                        <img src="/img/home/HNDSM+logo+2000+copy.png" alt="Logo HNDSM" />
                        <img src="/img/home/Troubadour_Logo_Black.png" alt="Logo Troubadour" />
                        <img src="/img/home/Nebraska_Furniture_Mart.png" alt="Logo NFM" />
                    </div>
                </div>

                {/* Why Add Live Video Section */}
                <div className="section-column">
                    <div className="t-color-2 f60 mf40 w80 mt100 t-center p20">
                        {t('homepage.why_add_video_title')}
                    </div>
                    <div className="f20 w80 mt50 t-center">
                        <div className="bold">{t('homepage.why_add_video_subtitle')}</div>
                        <br />
                        {t('homepage.why_add_video_description')}
                        <br />
                        <div className="bold mt20 t-left">
                            {t('homepage.parla_live_video_platform')}
                        </div>
                        <ul className="ml50 mt20 t-left">
                            <li>{t('homepage.features.branding')}</li>
                            <li>{t('homepage.features.ecommerce_flow')}</li>
                            <li>{t('homepage.features.tech_stack')}</li>
                        </ul>
                    </div>
                </div>

                {/* Parla Solution Section */}
                <div className="section">
                    <div className="hero">
                        <div className="hero-side-by-side-wrapper mt50">
                            <div className="title left w50 t-color-2 f50 mf30 mb20">
                                {t('homepage.parla_solution_title')}
                            </div>
                            <div className="row">
                                <div className="hero-side-r w50 no-padding">
                                    <div className="text-info-panel">
                                        <div className="side-lits-item p20 mp0 mb20">
                                            <span className="bold7 f25">
                                                {t('homepage.feature_one_title')}
                                            </span>
                                            <span className="side-lits-item-text">
                                                {t('homepage.feature_one_description')}
                                            </span>
                                        </div>
                                        <div className="side-lits-item pr20 mp0 mb20">
                                            <span className="bold7 f25">
                                                {t('homepage.feature_two_title')}
                                            </span>
                                            <span className="side-lits-item-text">
                                                {t('homepage.feature_two_description')}
                                            </span>
                                        </div>
                                        <div className="side-lits-item p20 mp0 mb20">
                                            <span className="bold7 f25">
                                                {t('homepage.feature_three_title')}
                                            </span>
                                            <span className="side-lits-item-text">
                                                {t('homepage.feature_three_description')}
                                            </span>
                                        </div>
                                        <div className="side-lits-item pr20 mp0 mb20">
                                            <span className="bold7 f25">
                                                {t('homepage.feature_four_title')}
                                            </span>
                                            <span className="side-lits-item-text">
                                                {t('homepage.feature_four_description')}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="hero-side-l w50 t-center">
                                    <img
                                        src="/img/home/Parla_book appointment via website image.png"
                                        alt="Parla Live Commerce - Hero Image"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Final Section */}
                <div className="section-book-demo">
                    <div className='book-demo-title'>
                        {t('homepage.shoppers_question')}
                    </div>
                    <div className='book-demo-subtitle'>
                        {t('homepage.shoppers_question2')}
                    </div>
                    <div className="btn-wrap mt20 center-in p20">
                         <button className="btn btn-white" onClick={() => (document as ParlaDoc).parla.popup33()}>{t('homepage.book_demo')}</button>
                    </div>

                   
                    <div id="parlaretail-cta"></div>

                </div>
            </div>
            <Footer />
        </>
    );
}
